import {
  ChoiceGroup,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  Dropdown,
  IChoiceGroupOption,
  IconButton,
  IDropdownOption,
  Label,
  PrimaryButton,
  Stack,
  Toggle,
  TooltipHost,
} from '@fluentui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BlockUi from 'react-block-ui';
import { useDispatch, useSelector } from 'react-redux';
import { HealAPI } from '../../../healApi';
import { ToastParams } from '../../../redux/message/types';
import { RootState } from '../../../redux/types';
import { fetchCancelReasons } from '../../../redux/visits/actions';
import { Visit } from '../../../redux/visits/types';

interface Props {
  visit: Visit;
  onSuccess: (visit: Visit) => void;
  actions: {
    toast: (params: ToastParams) => void;
  };
  onCancel?: () => void;
}

const CancelVisitDialog: React.FC<Props> = ({ visit, onSuccess, onCancel, actions }) => {
  const [cancelReasonId, setCancelReasonId] = useState<string>();
  const [category, setCategory] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);
  const [notifyPatient, setNotifyPatient] = useState(false);

  const dispatch: any = useDispatch();
  const { cancelReasons } = useSelector((state: RootState) => state.visits);

  // Fetch cancel reasons
  useEffect(() => {
    if (!cancelReasons?.data && !cancelReasons?.isFetching) {
      dispatch(fetchCancelReasons());
    }
  }, [cancelReasons, dispatch]);

  // Select first reason when category is set/changed
  useEffect(() => {
    if (cancelReasons?.data && category) {
      setCancelReasonId(cancelReasons.data[category][0].id);
    }
  }, [cancelReasons, category]);

  // Compute categories for ChoiceGroup
  const categoryChoices = useMemo(() => {
    if (cancelReasons?.data) {
      return Object.keys(cancelReasons.data).map((category): IChoiceGroupOption => ({ key: category, text: category }));
    }
  }, [cancelReasons]);

  // Compute reasons map (grouped by category) for Dropdown
  const reasonsForDropdown = useMemo(() => {
    if (cancelReasons?.data) {
      const reasons: { [category: string]: IDropdownOption<{}>[] } = {};

      Object.entries(cancelReasons.data).forEach((entry) => {
        reasons[entry[0]] = entry[1].map((cancelReason): IDropdownOption => ({ key: cancelReason.id, text: cancelReason.label }));
      });

      return reasons;
    }
  }, [cancelReasons]);

  const onClickCancelVisit = useCallback(() => {
    if (cancelReasonId) {
      setIsSaving(true);
      HealAPI.cancelVisit(visit.visitCode, true, cancelReasonId, '', 'N/A', notifyPatient)
        .then((response) => {
          const visit = response.data?.visit;
          if (response.ok && visit) {
            actions.toast({ type: 'success', title: 'Success', content: 'Visit has been successfully canceled' });
            onSuccess(visit);
          }
        })
        .catch((error) => {
          actions.toast({ type: 'error', title: 'Error', content: error });
          setIsSaving(false);
        });
    }
  }, [visit, cancelReasonId, notifyPatient, actions, onSuccess]);

  return (
    <Dialog hidden={false} dialogContentProps={{ type: DialogType.largeHeader, title: 'Cancel visit: ' + visit.visitCode }} onDismiss={onCancel}>
      <BlockUi tag="div" blocking={isSaving}>
        <Stack tokens={{ childrenGap: 15 }}>
          {categoryChoices && (
            <ChoiceGroup
              selectedKey={category}
              options={categoryChoices}
              onChange={(_, option) => option && category !== option.key && setCategory(option.key)}
              label="Cancellation category:"
            />
          )}

          {reasonsForDropdown && (
            <Dropdown
              placeholder="(Select a category first)"
              label="Please select a reason for cancellation:"
              options={category ? reasonsForDropdown[category] : []}
              selectedKey={cancelReasonId}
              onChange={(_, option) => option && setCancelReasonId(option.key as string)}
              disabled={!category}
            />
          )}

          <Stack>
            <Stack horizontal verticalAlign="center">
              <Label>Send patient notifications</Label>
              <TooltipHost content="This will prevent patient from being notified visit was canceled.">
                <IconButton iconProps={{ iconName: 'Info' }} title="Cancel silently info" ariaLabel="Cancel silently info" />
              </TooltipHost>
            </Stack>
            <Toggle onText="Yes" offText="No" checked={notifyPatient} onChange={() => setNotifyPatient((prevValue) => !prevValue)} />
          </Stack>

          <DialogFooter>
            <PrimaryButton onClick={onClickCancelVisit} disabled={!cancelReasonId} data-tid="btn_cancelVisit_ok">
              Ok
            </PrimaryButton>
            <DefaultButton onClick={onCancel}>Cancel</DefaultButton>
          </DialogFooter>
        </Stack>
      </BlockUi>
    </Dialog>
  );
};

export default CancelVisitDialog;
